import { lazy, Suspense } from 'react'
import { Navigate, Route, createRoutesFromElements } from 'react-router-dom'

import Home from '@/pages/home'
import { ResetPasswordRoute, VerifyEmailRoute, ConfirmEmailChangeRoute } from '@/features/auth'

import App from './App'

const FacesPage = lazy(() => import('@/pages/faces/ui/FacesPage'))
const CustomContentPage = lazy(() => import('@/pages/custom-content/ui/CustomContentPage'))
const Creation = lazy(() => import('@/pages/creation/ui/CreationPage'))
const CharacterPage = lazy(() => import('@/pages/character/ui/CharacterPage'))
const SettingsPage = lazy(() => import('@/pages/settings/ui/SettingsPage'))
const SharedCharacterPage = lazy(() => import('@/pages/shared/ui/SharedCharacterPage'))
const AboutPage = lazy(() => import('@/pages/about/ui/AboutPage'))
const PartyDetailPage = lazy(() => import('@/pages/parties/ui/PartyDetailPage'))
const JoinPartyPage = lazy(() => import('@/pages/parties/ui/JoinPartyPage'))

/**
 * The route tree, shared between the real app (`index.tsx`, via `createBrowserRouter`)
 * and `App.test.tsx` (via `createMemoryRouter`) so both exercise the exact same routes.
 * A data router (rather than `<Routes>`) is what `Link`'s `viewTransition` prop needs to
 * actually run `document.startViewTransition` — with a plain `<BrowserRouter>` it's a no-op.
 */
export const routes = createRoutesFromElements(
  <Route path="/" element={<App />}>
    <Route index element={<Home />} />
    {/*
      The three links PocketBase mails. Supabase put a magic link in the URL hash and
      fired an event, so none of these needed a route; a token in a path does. The
      templates are repointed at these in `pocketbase/pb_migrations/1789430400_tomf_schema.js`,
      away from PocketBase's own admin UI, which is where they point out of the box.
    */}
    <Route path="reset/:token" element={<ResetPasswordRoute />} />
    <Route path="verify/:token" element={<VerifyEmailRoute />} />
    <Route path="email/:token" element={<ConfirmEmailChangeRoute />} />
    <Route
      path="faces"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <FacesPage />
        </Suspense>
      }
    />
    <Route path="characters" element={<Navigate to="/faces" replace />} />
    <Route path="parties" element={<Navigate to="/faces" replace />} />
    <Route
      path="custom-content"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <CustomContentPage />
        </Suspense>
      }
    />
    <Route
      path="create"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <Creation />
        </Suspense>
      }
    />
    <Route
      path="character/:id"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <CharacterPage />
        </Suspense>
      }
    />
    <Route
      path="c/:shareId"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <SharedCharacterPage />
        </Suspense>
      }
    />
    <Route
      path="about"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <AboutPage />
        </Suspense>
      }
    />
    <Route
      path="parties/:partyId"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <PartyDetailPage />
        </Suspense>
      }
    />
    <Route
      path="join/:code"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <JoinPartyPage />
        </Suspense>
      }
    />
    <Route path="settings" element={<Navigate to="/settings/content" replace />} />
    <Route
      path="settings/:section"
      element={
        <Suspense fallback={<p className="text-muted-foreground">Loading…</p>}>
          <SettingsPage />
        </Suspense>
      }
    />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Route>,
)
